import {
  AutoComplete,
  FormInput,
  SwitchInput,
  useFormContext,
} from "@4uhub/lib4uhub";
import {
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { memo, MouseEvent, useCallback } from "react";
import ContentHeader from "./ContentHeader";
import ContentFooter from "./ContentFooter";
import { useTranslation } from "react-i18next";
import MainApi from "../../../../../services/mainApi.service";
import { TTemplateGeneralsForm } from "./templateGeneralSchema";
import { cssStyleInlineParser } from "../../../../../utils/cssStyleInlineParser";
import { useParams } from "react-router-dom";
import { useWatch } from "react-hook-form";

const GENERICS_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Generic/";
const genericService = new MainApi(GENERICS_ROUTE);

const translationPath = "chatBot.page.automations.templateGeneral.";

export const TemplateGeneralFields = memo(() => {
  const { id } = useParams();

  const { t } = useTranslation();

  const { control, setValue, getValues } =
    useFormContext<TTemplateGeneralsForm>();

  const useHtml = useWatch({
    control,
    name: "useHtml",
  });

  const handleEditorInputMode = useCallback(
    (_: MouseEvent<HTMLElement, globalThis.MouseEvent>, value: boolean) => {
      if (typeof value !== "boolean") return;

      if (useHtml) {
        const { contentHeader, contentFooter } = getValues();
        const headerFormatted = cssStyleInlineParser(contentHeader ?? "");
        const footerFormatted = cssStyleInlineParser(contentFooter ?? "");
        setValue("contentHeader", headerFormatted);
        setValue("contentFooter", footerFormatted);
      }
      setValue("useHtml", value);
    },
    [setValue, getValues, useHtml],
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} textAlign="end">
        <SwitchInput
          name="enable"
          label={t(translationPath + "enable") || "Enable"}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormInput
          name="name"
          label={t(translationPath + "name")}
          size="small"
          fullWidth
          autoFocus
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <AutoComplete
          name="templateGeneralType"
          params={{ identifier: "TemplateGeneralType" }}
          label={
            t(translationPath + "template_general_type") ||
            "Template General Type"
          }
          getOptionLabel={(option) => option.name}
          size="small"
          fullWidth
          request={genericService.list}
        />
      </Grid>

      <Grid item xs={12}>
        <Grid container gap={10} marginTop={5}>
          <ToggleButtonGroup
            value={useHtml}
            disabled={!!id}
            exclusive
            onChange={handleEditorInputMode}
            aria-label="text alignment"
            sx={(t) => ({
              width: 300,
              "& .MuiToggleButtonGroup-grouped": {
                padding: 1.5,
                "&.Mui-selected": {
                  backgroundColor: t.palette.primary.dark,
                  color: "white",
                  "& .MuiTypography-root": {
                    fontWeight: "bold",
                  },
                },
                "& .MuiTypography-root": {
                  fontSize: 16,
                },
              },
            })}
          >
            <ToggleButton value={false} sx={{ flex: 1 }}>
              <Typography variant="caption">
                {t(translationPath + "editor")}
              </Typography>
            </ToggleButton>

            <ToggleButton value={true} sx={{ flex: 1 }}>
              <Typography variant="caption">
                {t(translationPath + "html")}
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>

      <Grid item xs={12} sm={12}>
        <ContentHeader />
      </Grid>

      <Grid item xs={12} sm={12} marginTop={5}>
        <ContentFooter />
      </Grid>
    </Grid>
  );
});
